import React, { useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation, Trans} from "react-i18next";
import {Icon, Menu, MenuItem, Tag} from "@blueprintjs/core";

// Libs
import Button from "../common/components/button";
import {boxShadow, primaryColor, successColor, activeColor, orangeColor} from "../common/libs/theme-lib";

// Components
import ActivateUserDialog from "./activate-user-dialog";
import DeactivateUserDialog from "./deactivate-user-dialog";
import DeleteUserDialog from "./delete-user-dialog";
import NewUserDialog from "./new-user-dialog";
import Popover from "../common/components/popover";
import UpdateUserDialog from "./update-user-dialog";
import VirtualizedTable from "../common/components/virtualized-table";

// Actions
import {readUsers, sendWelcomeUser} from "./users-action";
import {readClients} from "../clients/clients-actions";

// Selectors
import {usersSelector, usersIsFetchingSelector, usersErrorSelector} from "./users-selectors";
import {clientsSelector, clientsIsFetchingSelector, clientsErrorSelector} from "../clients/clients-selectors";
import UpdatePasswordUserDialog from "./update-user-password-dialog";

// Component Selector
const usersPageSelector = createSelector(
    usersSelector,
    usersIsFetchingSelector,
    usersErrorSelector,
    clientsSelector, 
    clientsIsFetchingSelector, 
    clientsErrorSelector,
    (users, usersIsFetching, usersError, clients, clientsIsFetching, clientsError) => {
        users = users.toJS();
        users = (Array.isArray(users) && users) || [];
        return {
            users: users,
            usersIsFetching,
            usersError,
            clients: clients.toJS(),
            clientsIsFetching,
            clientsError
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readUsers: () => dispatch(readUsers()),
        sendWelcomeUser: (user) => dispatch(sendWelcomeUser(user)),
        readClients: () => dispatch(readClients())
    }
};

// Users popover
const UsersPopover = (props) => {
    const {user} = props;
    const {t} = useTranslation("users");

    // Redux
    const {sendWelcomeUser} = useReduxDispatch();

    // update user dialog state
    const [updateUserDialogIsOpen, setUpdateUserDialogIsOpen] = useState(false);

    // activate user dialog state
    const [activeUserDialogIsOpen, setActiveUserDialogIsOpen] = useState(false);

    // deactivate user dialog state
    const [deactivateUserDialogIsOpen, setDeactivateUserDialogIsOpen] = useState(false);

    // delete user dialog state
    const [deleteUserDialogIsOpen, setDeleteUserDialogIsOpen] = useState(false);

    // change password dialog state
    const [updatePasswordDialogIsOpen, setUpdatePasswordDialogIsOpen] = useState(false);

    return (
        <div style={{cursor: "pointer"}}>
            <Popover
                content={(
                    <Menu>
                        <MenuItem
                            text={t("popover.edit")}
                            onClick={() => setUpdateUserDialogIsOpen(true)}
                        />
                        {user.status === "active" && (
                            <MenuItem
                                text={t("deactivateUserDialog.deactivate")}
                                onClick={() => setDeactivateUserDialogIsOpen(true)}
                            />
                        )}
                        {user.status === "inactive" && (
                            <MenuItem
                                text={t("activateUserDialog.activate")}
                                onClick={() => setActiveUserDialogIsOpen(true)}
                            />
                        )}
                        {user.status === "pending" && (
                            <MenuItem
                                text={t("popover.email")}
                                onClick={() => sendWelcomeUser({_id: user._id})}
                            />
                        )}
                        <MenuItem 
                            text={t("popover.changePassword")}
                            onClick={() => setUpdatePasswordDialogIsOpen(true)}
                        />
                        <MenuItem
                            text={t("popover.delete")}
                            onClick={() => setDeleteUserDialogIsOpen(true)}
                            style={{color: primaryColor}}
                        />
                    </Menu>
                )}
                position={"bottom-end"}
            >
                <div>
                    <Icon icon="more" iconSize={20}/>
                </div>
            </Popover>
            <ActivateUserDialog
                isOpen={activeUserDialogIsOpen}
                onClose={() => setActiveUserDialogIsOpen(false)}
                user={user}
            />
            <DeactivateUserDialog
                isOpen={deactivateUserDialogIsOpen}
                onClose={() => setDeactivateUserDialogIsOpen(false)}
                user={user}
            />
            <DeleteUserDialog
                isOpen={deleteUserDialogIsOpen}
                onClose={() => setDeleteUserDialogIsOpen(false)}
                user={user}
            />
            <UpdateUserDialog
                isOpen={updateUserDialogIsOpen}
                onClose={() => setUpdateUserDialogIsOpen(false)}
                user={user}
            />
            <UpdatePasswordUserDialog 
                isOpen={updatePasswordDialogIsOpen}
                onClose={() => setUpdatePasswordDialogIsOpen(false)}
                user={user}
            />
        </div>
    );
};

const UsersPage = () => {
    // new user dialog state
    const [newUserDialogIsOpen, setNewUserDialogIsOpen] = useState(false);

    // Redux
    const {
        users, clients
    } = useSelector(state => usersPageSelector(state));
    const {readUsers, readClients} = useReduxDispatch();

    //i18n
    const {t} = useTranslation("users");

    // States
    const [userName, setUserName] = useState("")

    // show customer list when rendering component
    useEffect(() => {
        readUsers();
        readClients();
    }, []);

    let filterUsers = []

    if(!userName){
        filterUsers = users
    }else{
        filterUsers = users.filter( (user) => user.full_name.toLowerCase().includes(userName.toLocaleLowerCase()) )
    }

    const countActive = filterUsers.reduce((acc, cur) => cur.status === "active" ? ++acc : acc, 0);
    const countInactive = filterUsers.reduce((acc, cur) => cur.status === "inactive" ? ++acc : acc, 0);
    const countPending = filterUsers.reduce((acc, cur) => cur.status === "pending" ? ++acc : acc, 0);

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold"
                    }}>{t("title")}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                <div style={{
                    position: "relative",
                }}>
                    <div style={{
                        color: "black",
                        fontWeight: "bold",
                        fontSize: 12,
                        marginBottom: 5
                    }}>
                        {t("nameAndSurnames")}
                    </div>
                    <input
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        style={{ marginRight: 75, width: 200 }}
                    />
                </div>
                <Button
                    text={t("common:actions.newUser")}
                    action={() => setNewUserDialogIsOpen(true)}
                    intent="primary"
                />
            </div>
            <div style={{
                backgroundColor: "white",
                boxShadow: boxShadow,
                padding: 15,
                flex: 1,
                display: "flex",
                flexDirection: "column"
            }}>
                <div style={{marginBottom: 15, fontSize: 18, fontWeight: 100}}>
                    <Trans
                        i18nKey="users:usersTableHeader"
                        values={{count: filterUsers.length, countActive: countActive, countInactive: countInactive, countPending: countPending}}
                        components={[<strong>number</strong>]}
                    />
                </div>
                <VirtualizedTable
                    config={{
                        sortable: true,
                        showFooter: true
                    }}
                    columns={[{
                        Header: t("usersTableColumns.user_name"),
                        accessor: "full_name"
                    }, {
                        Header: t("usersTableColumns.email"),
                        accessor: "email"
                    }, {
                        Header: t("usersTableColumns.userType"),
                        accessor: "type",
                        type: "function",
                        nodeContent: (value) => {
                            return (
                                <div>
                                    {value === "admin" ? t("userType.admin") : t("userType.client")}
                                </div>
                            );
                        },
                        style: {
                            maxWidth: "180px"
                        }
                    },  {
                        Header: t("usersTableColumns.client"),
                        accessor: "client._id",
                        type: "function",
                        nodeContent: (value) => {
                            const res = clients.filter(client => client._id === value);
                            const name = res.length === 1 ? res[0].name : '--';
                            return (
                                <div>
                                    {name}
                                </div>
                            );
                        }
                    }, {
                        Header: t("usersTableColumns.date"),
                        accessor: "createdAt",
                        type: "date",
                        style: {
                            maxWidth: "130px"
                        }
                    }, {
                        Header: t("usersTableColumns.emailSent"),
                        accessor: "emails_sent",
                        type: "function",
                        nodeContent: (value) => {
                            return (
                                <div>
                                    {value >= 1 && (
                                        <div style={{
                                            position: "relative",
                                            top: "-2px"
                                        }}>
                                            <Icon icon="tick" iconSize={30} style={{marginRight: "20px"}} />
                                            <div style={{
                                                position: "absolute",
                                                top: "-5px",
                                                right: 0,
                                                backgroundColor: "white",
                                                border: "1px solid black",
                                                borderRadius: "25px",
                                                width: "20px",
                                                textAlign: "center",
                                                color: successColor
                                            }}>{value}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        },
                        style: {
                            maxWidth: "160px",
                            textAlign: "right"
                        }
                    }, {
                        Header: t("usersTableColumns.state"),
                        accessor: "status",
                        type: "function",
                        nodeContent: (value) => {
                            return (
                                <div style={{
                                    position: "absolute",
                                    top: "4px"
                                }}>
                                    {value === "active" && (
                                        <Tag
                                            style={{
                                                ...tagStyle,
                                                backgroundColor: activeColor
                                            }}
                                            large={true}
                                            intent={"success"}
                                        >
                                            {t("stateTag.active")}
                                        </Tag>
                                    )}
                                    {value === "inactive" && (
                                        <Tag
                                            style={tagStyle}
                                            large={true}
                                            intent={"danger"}
                                        >
                                            {t("stateTag.inactive")}
                                        </Tag>
                                    )}
                                    {value === "pending" && (
                                        <Tag
                                            style={{
                                                ...tagStyle,
                                                backgroundColor: orangeColor
                                            }}
                                            large={true}
                                        >
                                            {t("stateTag.pending")}
                                        </Tag>
                                    )}
                                </div>
                            );
                        },
                        style: {
                            paddingLeft: "20px",
                            maxWidth: "150px"
                        }
                    }, {
                        id: "actions",
                        Header: "",
                        accessor: "_id",
                        type: "function",
                        disableSortBy: true,
                        minWidth: 60,
                        width: 60,
                        style: {
                            maxWidth: 60,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        },
                        nodeContent: (value) => {
                            return (
                                <UsersPopover
                                    user={filterUsers.find((user) => user._id === value)}
                                />
                            );
                        }
                    }]}
                    data={filterUsers}
                />
            </div>
            <NewUserDialog
                isOpen={newUserDialogIsOpen}
                onClose={() => setNewUserDialogIsOpen(false)}
            />
        </>
    );
};

export default UsersPage;

const tagStyle = {
    minWidth: "100px",
    textAlign: "center"
};